export class UsageSeriesPoint {
  date!: string;
  requests!: number;
  credits!: number;
  costCents!: number;
  errors!: number;
  errorRate!: number;
  avgLatencyMs!: number;

  constructor(data: any = {}) {
    this.date = data.date;
    this.requests = data.requests;
    this.credits = data.credits;
    this.costCents = data.cost_cents;
    this.errors = data.errors;
    this.errorRate = data.error_rate;
    this.avgLatencyMs = data.avg_latency_ms;
  }
}

export class UsageEndpointStat {
  endpoint!: string;
  requests!: number;
  credits!: number;
  costCents!: number;
  errors!: number;
  errorRate!: number;
  avgLatencyMs!: number;

  constructor(data: any = {}) {
    this.endpoint = data.endpoint;
    this.requests = data.requests;
    this.credits = data.credits;
    this.costCents = data.cost_cents;
    this.errors = data.errors;
    this.errorRate = data.error_rate;
    this.avgLatencyMs = data.avg_latency_ms;
  }
}

export class UsageApiKeyStat {
  apiKeyId: string | null = null;  // null once the key has been deleted
  name!: string;                   // "Deleted key" if the key is gone
  prefix!: string;
  requests!: number;
  credits!: number;
  costCents!: number;

  constructor(data: any = {}) {
    this.apiKeyId = data.api_key_id ?? null;
    this.name = data.name;
    this.prefix = data.prefix;
    this.requests = data.requests;
    this.credits = data.credits;
    this.costCents = data.cost_cents;
  }
}

export class UsageTotals {
  requests!: number;
  creditsUsed!: number;
  costCents!: number;
  errors!: number;
  errorRate!: number;
  avgLatencyMs!: number;
  balanceCents!: number;   // current balance NOT scoped to the queried range
  currency!: string;

  constructor(data: any = {}) {
    this.requests = data.requests;
    this.creditsUsed = data.credits_used;
    this.costCents = data.cost_cents;
    this.errors = data.errors;
    this.errorRate = data.error_rate;
    this.avgLatencyMs = data.avg_latency_ms;
    this.balanceCents = data.balance_cents;
    this.currency = data.currency;
  }
}

/** The % change of each `totals` metric vs. the equal-length window
 *  immediately before `range.from`, computed server-side and returned on
 *  every /usage response. Kept nullable on UsageResult regardless, so a
 *  caller that doesn't need it (or an older/unexpected response shape)
 *  degrades to no delta instead of breaking. */
export class UsageDeltas {
  requestsPct!: number;
  creditsUsedPct!: number;
  errorRatePct!: number;
  avgLatencyMsPct!: number;

  constructor(data: any = {}) {
    this.requestsPct = data.requests_pct;
    this.creditsUsedPct = data.credits_used_pct;
    this.errorRatePct = data.error_rate_pct;
    this.avgLatencyMsPct = data.avg_latency_ms_pct;
  }
}

export class UsageResult {
  range!: { from: string; to: string };
  granularity!: string;
  filters: Record<string, string> = {};
  series: UsageSeriesPoint[] = [];       // zero-filled, one entry per day
  totals!: UsageTotals;
  byEndpoint: UsageEndpointStat[] = [];  // sorted by requests desc
  byApiKey: UsageApiKeyStat[] = [];
  deltas: UsageDeltas | null = null;

  constructor(data: any = {}) {
    this.range = data.range;
    this.granularity = data.granularity;
    this.filters = data.filters ?? {};
    this.series = (data.series ?? []).map((s: object) => new UsageSeriesPoint(s));
    this.totals = new UsageTotals(data.totals);
    this.byEndpoint = (data.by_endpoint ?? []).map((e: object) => new UsageEndpointStat(e));
    this.deltas = data.deltas ? new UsageDeltas(data.deltas) : null;
    this.byApiKey = (data.by_api_key ?? []).map((k: object) => new UsageApiKeyStat(k));
  }
}
